// Arrow-key cell navigation for the tabular template grid.
//
// The hook is intentionally DOM-driven rather than state-driven: it
// queries the grid container for elements decorated with
// `data-grid-cell` (and the focused-input form control inside) and
// moves focus to the neighbour cell on arrow keys / Tab / Enter.
//
// Why DOM-driven: cell editors are heterogeneous (text, number, date,
// dropdown, checkbox), each rendered by the existing capture-tabular
// component. Tracking an "active cell" in React state would require
// every editor to participate in a focus protocol. Reading `data-*`
// attributes from the focused element is one extra attribute per cell.
//
// Cell address attributes (applied to the wrapping <td>):
//   data-grid-cell="<rowKey>::<columnKey>"
//   data-grid-row="<rowKey>"
//   data-grid-col="<columnKey>"
//
// Keys handled:
//   ArrowLeft/Right        : prev/next cell in row
//   ArrowUp/Down           : same column, prev/next row
//   Tab / Shift+Tab        : next / prev cell (wraps rows)
//   Enter                  : next row, same column. Shift+Enter prev.
//   Escape                 : blur the active input (commit on blur).
//
// Modifier keys with text selection (Cmd/Ctrl + arrow, Shift + arrow
// for selection) pass through unmodified so native text-input UX works.

import { useEffect } from 'react'
import type { RefObject } from 'react'

export interface KeyboardNavOptions {
  enabled: boolean
}

interface CellAddr { row: string; col: string }

function parseAddr(el: HTMLElement | null): CellAddr | null {
  if (!el) return null
  const row = el.dataset.gridRow
  const col = el.dataset.gridCol
  if (!row || !col) return null
  return { row, col }
}

function findCell(container: HTMLElement, addr: CellAddr): HTMLElement | null {
  // Escape any quotes in the address so the selector is safe.
  const sel = `[data-grid-cell="${cssEscape(`${addr.row}::${addr.col}`)}"]`
  return container.querySelector(sel) as HTMLElement | null
}

function cssEscape(s: string): string {
  // CSS.escape isn't ubiquitous in test environments and we control the
  // shape of the id (record uuid + column db_name), so a lightweight
  // escape is enough.
  return s.replace(/(["\\])/g, '\\$1')
}

function focusInsideCell(cell: HTMLElement): void {
  const target = cell.querySelector<HTMLElement>(
    'input, select, textarea, button',
  )
  if (target) {
    target.focus()
    // If it's a text input, select all so typing replaces.
    if (target instanceof HTMLInputElement
        && (target.type === 'text' || target.type === 'number'
            || target.type === 'date' || target.type === 'datetime-local'
            || target.type === 'search')) {
      try { target.select() } catch { /* ignore */ }
    }
  } else {
    // Fall back to focusing the cell itself (it must be tabindex>=0).
    cell.focus()
  }
}

function allRowsAndCols(container: HTMLElement): { rows: string[]; cols: string[] } {
  const rowSet = new Set<string>()
  const colOrder: string[] = []
  const seenCols = new Set<string>()
  const cells = container.querySelectorAll<HTMLElement>('[data-grid-cell]')
  cells.forEach((el) => {
    const r = el.dataset.gridRow
    const c = el.dataset.gridCol
    if (r) rowSet.add(r)
    if (c && !seenCols.has(c)) {
      seenCols.add(c)
      colOrder.push(c)
    }
  })
  // Preserve DOM order for rows by re-walking.
  const rows: string[] = []
  const seenRows = new Set<string>()
  cells.forEach((el) => {
    const r = el.dataset.gridRow
    if (r && !seenRows.has(r)) {
      seenRows.add(r)
      rows.push(r)
    }
  })
  return { rows, cols: colOrder }
}

export function useGridKeyboardNav(
  containerRef: RefObject<HTMLElement | null>,
  options: KeyboardNavOptions,
): void {
  useEffect(() => {
    if (!options.enabled) return
    const container = containerRef.current
    if (!container) return

    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null
      if (!target) return
      const cell = target.closest<HTMLElement>('[data-grid-cell]')
      if (!cell || !container.contains(cell)) return
      const addr = parseAddr(cell)
      if (!addr) return

      // Don't hijack text-selection / cursor-positioning when the user
      // is editing inside a multi-line textarea.
      if (target instanceof HTMLTextAreaElement) return

      // For single-line inputs, only intercept arrow-left/right when
      // caret is at the boundary so typing UX feels normal.
      const isInput = target instanceof HTMLInputElement
      const caretAtStart = isInput && target.selectionStart === 0 && target.selectionEnd === 0
      const caretAtEnd = isInput && target.selectionStart === target.value.length
        && target.selectionEnd === target.value.length

      let nextAddr: CellAddr | null = null
      let preventDefault = false

      switch (e.key) {
        case 'Escape':
          if (target instanceof HTMLElement) target.blur()
          break
        case 'Tab': {
          const { rows, cols } = allRowsAndCols(container)
          if (rows.length === 0 || cols.length === 0) return
          const rIdx = rows.indexOf(addr.row)
          const cIdx = cols.indexOf(addr.col)
          if (rIdx === -1 || cIdx === -1) return
          let nr = rIdx
          let nc = cIdx + (e.shiftKey ? -1 : 1)
          if (nc >= cols.length) { nc = 0; nr = Math.min(rIdx + 1, rows.length - 1) }
          else if (nc < 0) { nc = cols.length - 1; nr = Math.max(rIdx - 1, 0) }
          nextAddr = { row: rows[nr], col: cols[nc] }
          preventDefault = true
          break
        }
        case 'Enter': {
          if (e.altKey || e.ctrlKey || e.metaKey) return
          const { rows } = allRowsAndCols(container)
          const rIdx = rows.indexOf(addr.row)
          if (rIdx === -1) return
          const nr = e.shiftKey ? Math.max(rIdx - 1, 0) : Math.min(rIdx + 1, rows.length - 1)
          if (nr === rIdx) return
          nextAddr = { row: rows[nr], col: addr.col }
          preventDefault = true
          break
        }
        case 'ArrowUp':
        case 'ArrowDown': {
          if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return
          // For native date/select pickers, ArrowUp/Down adjusts the
          // value — let them keep that UX.
          if (target instanceof HTMLSelectElement) return
          if (target instanceof HTMLInputElement
              && (target.type === 'number' || target.type === 'date'
                  || target.type === 'datetime-local' || target.type === 'time')) {
            return
          }
          const { rows } = allRowsAndCols(container)
          const rIdx = rows.indexOf(addr.row)
          if (rIdx === -1) return
          const dir = e.key === 'ArrowUp' ? -1 : 1
          const nr = Math.max(0, Math.min(rows.length - 1, rIdx + dir))
          if (nr === rIdx) return
          nextAddr = { row: rows[nr], col: addr.col }
          preventDefault = true
          break
        }
        case 'ArrowLeft':
        case 'ArrowRight': {
          if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return
          // Only jump when caret is at the boundary of the input value.
          if (isInput && (target as HTMLInputElement).type !== 'checkbox') {
            if (e.key === 'ArrowLeft' && !caretAtStart) return
            if (e.key === 'ArrowRight' && !caretAtEnd) return
          }
          const { cols } = allRowsAndCols(container)
          const cIdx = cols.indexOf(addr.col)
          if (cIdx === -1) return
          const dir = e.key === 'ArrowLeft' ? -1 : 1
          const nc = Math.max(0, Math.min(cols.length - 1, cIdx + dir))
          if (nc === cIdx) return
          nextAddr = { row: addr.row, col: cols[nc] }
          preventDefault = true
          break
        }
        default:
          return
      }

      if (preventDefault) e.preventDefault()
      if (nextAddr) {
        const next = findCell(container, nextAddr)
        if (next) focusInsideCell(next)
      }
    }

    container.addEventListener('keydown', handler)
    return () => { container.removeEventListener('keydown', handler) }
  }, [containerRef, options.enabled])
}
